import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { programmesApi } from '../../api/programmes';
import { projectsApi } from '../../api/projects';
import { masterDataApi } from '../../api/masterData';
import { usersApi } from '../../api/admin/users';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { Modal } from '../../components/Modal';
import { ReturnAction } from '../../components/ReturnAction';
import { useToast } from '../../components/Toast';
import { usePermission } from '../../auth/AuthContext';

const formSchema = z.object({
  name: z.string().min(3, 'Name must be at least 3 characters.'),
  donorId: z.string().optional(),
  managerId: z.string().optional(),
});
type FormValues = z.infer<typeof formSchema>;

export function ProgrammeDetailPage() {
  const { programmeId } = useParams<{ programmeId: string }>();
  const id = Number(programmeId);
  const navigate = useNavigate();
  const canManage = usePermission('programmes.manage');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showEditForm, setShowEditForm] = useState(false);

  const programmeQuery = useQuery({ queryKey: ['programme', id], queryFn: () => programmesApi.get(id) });
  const projectsQuery = useQuery({ queryKey: ['projects'], queryFn: () => projectsApi.list() });
  const donorsQuery = useQuery({ queryKey: ['master-donors'], queryFn: () => masterDataApi.donors(), enabled: showEditForm });
  const usersQuery = useQuery({ queryKey: ['admin-users'], queryFn: () => usersApi.list(), enabled: showEditForm });
  const docsQuery = useQuery({ queryKey: ['programme-documents', id], queryFn: () => genericDocumentApi.listFor('programme', id) });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(formSchema) });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['programme', id] });

  const updateMutation = useMutation({
    mutationFn: (values: FormValues) => programmesApi.update(id, {
      name: values.name, donorId: values.donorId ? Number(values.donorId) : null, managerId: values.managerId ? Number(values.managerId) : null,
    }),
    onSuccess: () => { showSuccess('Programme updated.'); invalidate(); setShowEditForm(false); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to update programme.'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => programmesApi.delete(id),
    onSuccess: () => { showSuccess('Programme deleted.'); navigate('/programmes'); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to delete programme.'),
  });

  const submitMutation = useMutation({
    mutationFn: () => programmesApi.submit(id),
    onSuccess: () => { showSuccess('Submitted for approval.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit.'),
  });

  const decideMutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => programmesApi.decide(id, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision.'),
  });

  const linkedProjects = projectsQuery.data?.data.filter((p) => p.programme_id === id) ?? [];

  const openEdit = () => {
    if (!programmeQuery.data) return;
    reset({
      name: programmeQuery.data.data.name,
      donorId: programmeQuery.data.data.donor_id ? String(programmeQuery.data.data.donor_id) : '',
      managerId: programmeQuery.data.data.manager_id ? String(programmeQuery.data.data.manager_id) : '',
    });
    setShowEditForm(true);
  };

  const handleDelete = () => {
    if (window.confirm('Delete this programme? This only works while it is still in draft with no linked projects.')) {
      deleteMutation.mutate();
    }
  };

  return (
    <div className="page">
      <Link to="/programmes">&larr; Back to Programmes</Link>

      {programmeQuery.data && (
        <>
          <RecordHeader
            title={programmeQuery.data.data.name}
            readOnly={!canManage}
            entityType="programme"
            entityId={id}
            fields={[
              { label: 'Reference', value: programmeQuery.data.data.programme_reference },
              { label: 'Status', value: <StatusBadge status={programmeQuery.data.data.status} /> },
            ]}
            actions={canManage && (
              <span className="row-actions">
                {['draft', 'returned'].includes(programmeQuery.data.data.status) && (
                  <>
                    <button className="btn-secondary" onClick={openEdit}>Edit</button>
                    <button className="btn-primary" onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>{submitMutation.isPending ? 'Submitting…' : 'Submit for Approval'}</button>
                    <button className="button-danger" onClick={handleDelete} disabled={deleteMutation.isPending}>Delete</button>
                  </>
                )}
                {programmeQuery.data.data.status === 'pending_approval' && (
                  <>
                    <button className="btn-primary" onClick={() => decideMutation.mutate('approved')} disabled={decideMutation.isPending}>Approve</button>
                    <button className="button-danger" onClick={() => decideMutation.mutate('rejected')} disabled={decideMutation.isPending}>Reject</button>
                    <ReturnAction onReturn={(comment) => programmesApi.returnForRedraft(id, comment)} queryKeysToInvalidate={[['programme', id]]} />
                  </>
                )}
              </span>
            )}
          />

          <WorkspaceTabs
            tabs={[
              {
                key: 'budget_rollup',
                label: 'Budget Rollup',
                content: (
                  <>
                    <section className="card">
                      <h2>Budget rollup</h2>
                      <div className="stat">
                        <div className="stat-value">{Number(programmeQuery.data.data.budget_rollup ?? 0).toLocaleString()}</div>
                        <div className="stat-label">Total across all linked projects</div>
                      </div>
                    </section>
                    <CommentsThread entityType="programme" entityId={id} section="budget_rollup" heading="Comments for Budget Rollup" />
                  </>
                ),
              },
              {
                key: 'linked_projects',
                label: 'Projects Under This Programme',
                content: (
                  <>
                    <div className="panel">
                      <SectionHeader title="Projects Under This Programme" />
                      <div className="panel-body">
                      <DataTable
                        rows={linkedProjects}
                        rowKey={(p) => p.id}
                        emptyMessage="No projects linked to this programme yet."
                        columns={[
                          { header: 'Reference', render: (p) => <Link to={`/projects/${p.id}`}>{p.project_reference}</Link> },
                          { header: 'Name', render: (p) => p.project_name },
                          { header: 'Status', render: (p) => <StatusBadge status={p.status} /> },
                          { header: 'Budget', render: (p) => `${p.currency_code} ${Number(p.budget_total).toLocaleString()}` },
                        ]}
                      />
                      </div>
                    </div>
                    <CommentsThread entityType="programme" entityId={id} section="linked_projects" heading="Comments for Projects Under This Programme" />
                  </>
                ),
              },
              {
                key: 'documents',
                label: 'Documents',
                content: (
                  <DocumentsSection
                    documents={docsQuery.data?.data ?? []}
                    downloadUrl={(documentId) => genericDocumentApi.downloadUrl('programme', documentId)}
                    upload={(input) => genericDocumentApi.uploadFor('programme', id, input)}
                    onDelete={(documentId) => genericDocumentApi.deleteFor('programme', documentId)}
                    queryKeyToInvalidate={['programme-documents', id]}
                    hrCategories={false}
                  />
                ),
              },
              {
                key: 'tracking',
                label: 'Tracking',
                content: <WorkflowTrackingTab entityType="programme" entityId={id} />,
              },
            ]}
          />
        </>
      )}

      {showEditForm && (
        <Modal title="Edit Programme" onClose={() => setShowEditForm(false)}>
          <form onSubmit={handleSubmit((values) => updateMutation.mutate(values))}>
            <label>
              Name
              <input {...register('name')} />
              {errors.name && <span className="field-error">{errors.name.message}</span>}
            </label>
            <label>
              Donor (optional)
              <select {...register('donorId')}>
                <option value="">- none -</option>
                {donorsQuery.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </label>
            <label>
              Programme Manager (optional)
              <select {...register('managerId')}>
                <option value="">- none -</option>
                {usersQuery.data?.data.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={updateMutation.isPending}>{updateMutation.isPending ? 'Saving…' : 'Save'}</button>
              <button type="button" className="btn-secondary" onClick={() => setShowEditForm(false)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
